/**
 * 区域级联树 —— 纯函数工具（无状态、无副作用）
 * ==================================================
 *
 * 大屏的「区域 / 校区」两级入口（`BattleScreenPortal.vue` 配置页、
 * `BattleScreen.vue` 抽屉）共用这里的函数，保证两边口径一致。
 *
 * 🔴 三条约定（改之前先读）
 *
 * 1. **联动交给 el-cascader 原生做**：多选一律 `checkStrictly: false`。
 *    Element Plus 在非严格模式下：
 *      · 勾父节点 → `Node.doCheck` 走 `broadcast()`，全部后代一起打勾；
 *      · 取消父节点 → 全部后代一起取消；
 *      · 子级全选 → `onChildCheck()` 把父级置为 checked；
 *      · 只选一部分 → 父级 `indeterminate`（半选）。
 *    这正是用户要的"上级勾选、下级全选"。**不要再自己写"手动下推 + 记账回收"**：
 *    下推产物被组件原样回传后，无法区分"用户手点的"与"系统补的"，
 *    取消父节点时下级会残留（历史 bug，见 `scripts/verify-cascader-linkage.mjs`）。
 *
 * 2. **cascader 的 v-model 只认叶子 id**：`checkStrictly=false` 时组件内部
 *    传 `leafOnly=true`（`calculateCheckedValue` / `getNodeByValue`），
 *    中间层（省/市/区县）id 既不打勾也不显示标签。
 *    所以给 cascader 绑值前必须 `expandToLeaves()`。
 *
 * 3. **后端按任意层级展开**：`BattleScreenMapper.expandCampusIds` 接受
 *    省/市/区县/校区混合 id，最终收敛到叶子校区（flag 只在叶子可靠）。
 *    因此写 URL / 发给后端时用 `collapseToTopNodes()` 折叠成"最高代表节点"，
 *    既缩短地址（勾一个省可从 600+ 个 id 降到 1 个），语义又完全不变。
 */

/** 建 id → 节点 索引（含所有层级） */
export function indexById(tree) {
  const byId = Object.create(null);
  const walk = nodes => (nodes || []).forEach(n => {
    byId[n.id] = n;
    if (n.children && n.children.length) walk(n.children);
  });
  walk(tree);
  return byId;
}

/** 收集某个节点下的全部叶子 id（自身是叶子则返回自身） */
export function collectLeafIds(node, out) {
  const sink = out || new Set();
  const ch = (node && node.children) || [];
  if (!ch.length) { if (node) sink.add(node.id); return sink; }
  ch.forEach(c => collectLeafIds(c, sink));
  return sink;
}

/**
 * el-cascader 取值归一：
 *  - `emitPath:false` → `[1, 2]` 或单选标量 `1`
 *  - `emitPath:true`  → `[[1, 2], [1, 3]]`（历史/其他页面配置）
 * 统一成 id 数组，并剔除空值。
 */
export function toIdArray(v) {
  const arr = Array.isArray(v) ? v : (v == null || v === '' ? [] : [v]);
  return arr
    .map(x => (Array.isArray(x) ? x[x.length - 1] : x))
    .filter(x => x !== null && x !== undefined && x !== '');
}

/**
 * 任意层级 id → 叶子 id 数组（去重；树里不存在的 id 直接丢弃）。
 * 用途：给 `checkStrictly:false` 的 cascader 绑值。
 */
export function expandToLeaves(tree, ids) {
  const byId = indexById(tree);
  const out = new Set();
  toIdArray(ids).forEach(id => {
    const node = byId[id];
    if (node) collectLeafIds(node, out);
  });
  return Array.from(out);
}

/**
 * 叶子 id → 「最简整片覆盖」节点 id 集合。
 * 某节点的全部后代叶子都被选中时，用该节点自身代表（不再往下展开）。
 *
 * 例：郑州市下 83 个校区全选 → 返回 `[郑州市id]` 而不是 83 个校区 id。
 * 后端 `expandCampusIds` 会把它重新展开成同样的 83 个校区，统计口径不变。
 *
 * ⚠️ 失败保护：区域树没拉到时（接口挂了）**原样返回入参**，不做任何丢弃。
 *    否则"折叠"会退化成"清空筛选"，把用户选定的小范围静默放大成全量 —— 这是错数，
 *    比多传几个 id 危险得多。
 */
export function collapseToTopNodes(tree, ids) {
  const raw = toIdArray(ids);
  if (!(tree || []).length) return raw;
  const leaves = new Set(expandToLeaves(tree, raw));
  if (!leaves.size) return [];
  const out = [];
  const walk = nodes => (nodes || []).forEach(n => {
    const ch = n.children || [];
    if (!ch.length) {
      if (leaves.has(n.id)) out.push(n.id);
      return;
    }
    const sub = collectLeafIds(n);
    if (!sub.size) return;                 // 空子树（理论不该有）：不下发也不递归
    let covered = true;
    for (const leaf of sub) {
      if (!leaves.has(leaf)) { covered = false; break; }
    }
    if (covered) { out.push(n.id); return; }  // 整片命中 → 用该节点代表
    walk(ch);
  });
  walk(tree);
  return out;
}

/**
 * el-cascader 搜索过滤。
 * `node.text` 是完整路径（如 "河南省 / 郑州市 / 金水区 / 七里河校区-Z"），
 * 所以输入区域名能命中其下全部校区，输入校区名则精确命中。
 */
export function areaFilterMethod(node, keyword) {
  const kw = (keyword || '').toString().trim().toLowerCase();
  if (!kw) return true;
  return (node.text || '').toLowerCase().includes(kw);
}
